/* global google */
import {
  findComponent,
  formatAutoSuggestionResult,
  parseAddressComponents,
  parsePlaceAddressComponents,
} from './helpers'
import { logGoogleMapsError, waitForGoogleMaps } from '../../util/useGoogle'

interface Waypoint {
  address: string | null
  city: string | null
  lat: number | null
  long: number | null
}

export async function createToken() {
  try {
    await waitForGoogleMaps()
    const placesLib = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary
    return new placesLib.AutocompleteSessionToken()
  } catch (err) {
    logGoogleMapsError('Failed to create AutocompleteSessionToken:', err)
    return undefined
  }
}

export async function geocode({ address }: { address: string }): Promise<google.maps.GeocoderResult[]> {
  try {
    await waitForGoogleMaps()
    const geocodeLib = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary
    const geocoder = new geocodeLib.Geocoder()

    const { results } = await geocoder.geocode({ address: address })

    return results
  } catch (err) {
    logGoogleMapsError('Failed to fetch geocode:', err)
    return []
  }
}

export async function reverseGeocode(location: {
  lat: number
  lng: number
}): Promise<google.maps.GeocoderResult[] | null> {
  try {
    await waitForGoogleMaps()
    const geocodeLib = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary
    const geocoder = new geocodeLib.Geocoder()

    const { results } = await geocoder.geocode({ location })

    return results
  } catch (err) {
    logGoogleMapsError('Failed to fetch reverseGeocode:', err)
    return null
  }
}

export async function getDirections(
  origin: Waypoint,
  destination: Waypoint
): Promise<google.maps.DirectionsResult | null> {
  const formatWaypoint = (wp: Waypoint): google.maps.LatLngLiteral | string => {
    if (wp.lat != null && wp.long != null) {
      return { lat: wp.lat, lng: wp.long }
    } else if (wp.address && wp.city) {
      return `${wp.address} ${wp.city}`
    }
    return ''
  }

  try {
    await waitForGoogleMaps()
    const routesLib = (await google.maps.importLibrary('routes')) as google.maps.RoutesLibrary
    const directionsService = new routesLib.DirectionsService()

    const result = await directionsService.route({
      origin: formatWaypoint(origin),
      destination: formatWaypoint(destination),
      language: 'fi',
      travelMode: google.maps.TravelMode.DRIVING,
    })

    return result
  } catch (err) {
    logGoogleMapsError('Failed to fetch directions:', err)
    return null
  }
}

export async function getAutocomplete(input: string, token?: google.maps.places.AutocompleteSessionToken) {
  try {
    await waitForGoogleMaps()
    const placesLib = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary
    const result = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: input,
      language: 'fi',
      includedRegionCodes: ['fi'],
      sessionToken: token,
    })

    return formatAutoSuggestionResult(result.suggestions)
  } catch (err) {
    logGoogleMapsError('Failed to fetch AutocompleteSuggestions:', err)
    return null
  }
}

export async function getPlaceDetails(placeId: string): Promise<google.maps.places.Place | null> {
  try {
    await waitForGoogleMaps()
    const placesLib = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary
    const place = new placesLib.Place({
      id: placeId,
      requestedLanguage: 'fi',
      requestedRegion: 'fi',
    })
    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] })
    return place
  } catch (err) {
    logGoogleMapsError('Failed to fetch place details:', err)
    return null
  }
}

export async function getAutoSuggestPlaceDetails(placeId: string, includeNameInAddress: boolean) {
  const place = await getPlaceDetails(placeId)

  if (!place || !place.addressComponents) {
    return null
  }

  const { address, city, zipcode, name } = parsePlaceAddressComponents(place.displayName || '', place.addressComponents, includeNameInAddress)
  const location = {
    lat: (place.location && place.location.lat()) || 0,
    long: (place.location && place.location.lng()) || 0,
  }

  let finalPostalCode = await zipCodeFallback(zipcode, location)

  return {
    address,
    city,
    zipcode: finalPostalCode,
    name,
    location,
  }
}

export async function getGeocodeResult(search: string) {
  const results = await geocode({ address: search })

  if (!results) {
    return null
  }
  const [address, city, zipcode] = parseAddressComponents(results[0])
  return {
    address,
    city,
    zipcode,
    location: { lat: results[0]?.geometry?.location?.lat(), long: results[0]?.geometry?.location?.lng() },
  }
}

export async function getReverseGeocodeResult(location: { lat: number; lng: number }) {
  const results = await reverseGeocode(location)

  if (!results) {
    return null
  }

  const [address, city, zipcode] = parseAddressComponents(results[0])
  
  return { address, city, zipcode }
}

async function zipCodeFallback(zipcode: string, location: { lat: number; long: number }) {
  let finalPostalCode = zipcode

  if (finalPostalCode) {
    return finalPostalCode
  }
  
  const reverseGeocodeResult = await reverseGeocode({ lat: location.lat, lng: location.long })
  if (reverseGeocodeResult) {
    const component = findComponent(reverseGeocodeResult[0], 'postal_code')
    finalPostalCode = component ? component.long_name : ''
  }

  return finalPostalCode
}